import { MailFilled } from "@ant-design/icons";
import { DOCS_URLS, getEnvironment } from "@p0security/frontend-core";
import { AwsLambdaNotifierComponents } from "@p0security/integrations-aws-lambda-notifier-shared/components";
import { AwsLambdaIconUrl } from "@p0security/integrations-aws-lambda-notifier-shared/registry";
import { AwsS3AuditIconUrl } from "@p0security/integrations-aws-s3-audit-shared";
import { AwsSqsIconUrl } from "@p0security/integrations-aws-sqs-shared";
import { DatadogIconUrl } from "@p0security/integrations-datadog-shared";
import { JiraTrackerComponents } from "@p0security/integrations-jira-tracker-shared/components";
import { JiraTrackerRegistration } from "@p0security/integrations-jira-tracker-shared/registry";
import { PagerdutyIconUrl } from "@p0security/integrations-pagerduty-shared";
import { SplunkIconUrl } from "@p0security/integrations-splunk-events-shared";
import { visibleComponentKeys } from "@p0security/shared/install/options";
import { JiraIconUrl } from "@p0security/shared/integrations/registry";
import type { LDFlagSet } from "launchdarkly-react-client-sdk";

import { SlackLogo } from "./Slack/Slack";
import { TeamsLogo } from "./Teams/Teams";
import { registryToIntegrationData } from "./from-registry.ts";
import { IntegrationData } from "./types";

export const IncidentIoIconUrl = "https://incident.io/favicon.ico";
export const GrafanaIrmIconUrl =
  "https://grafana.com/static/assets/img/fav32.png";

const jira: IntegrationData = {
  key: "jira",
  label: "Jira (Legacy)",
  icon: JiraIconUrl,
  docs: {
    install: DOCS_URLS.JIRA_INSTALL,
  },
};

const jiraTracker: IntegrationData = {
  key: "jira-tracker",
  label: JiraTrackerRegistration.label,
  icon: JiraTrackerRegistration.logoUrl,
  availableComponents: visibleComponentKeys(
    JiraTrackerComponents,
    getEnvironment().environmentType
  ),
  docs: {},
};

/** The tracker cards a tenant sees. The `show-legacy-jira-tracker` flag picks
 * one Jira tracker: Jira (Legacy), the Atlassian Connect app, when on, and
 * Jira otherwise. */
export const trackers = (
  flags?: Record<string, boolean>
): IntegrationData[] => [flags?.showLegacyJiraTracker ? jira : jiraTracker];

export const siems: IntegrationData[] = [
  {
    key: "splunk",
    label: "Splunk",
    icon: SplunkIconUrl,
    docs: {
      install: DOCS_URLS.SPLUNK_INSTALL,
    },
    availableComponents: ["audit-log"],
  },
  {
    key: "datadog",
    label: "Datadog",
    icon: DatadogIconUrl,
    docs: {
      install: "",
    },
    availableComponents: ["audit-log"],
  },
  {
    key: "aws-sqs",
    label: "Amazon SQS",
    icon: AwsSqsIconUrl,
    docs: {
      install: DOCS_URLS.AWS_SQS_INSTALL,
    },
    availableComponents: ["audit-log"],
  },
  {
    key: "aws-s3-audit",
    label: "Amazon S3 (audit logs)",
    icon: AwsS3AuditIconUrl,
    docs: {
      install: DOCS_URLS.AWS_S3_AUDIT_INSTALL,
    },
    availableComponents: ["audit-log"],
  },
];

export const approvers: IntegrationData[] = [
  {
    key: "pagerduty",
    label: "Pagerduty",
    icon: PagerdutyIconUrl,
    docs: {
      install: DOCS_URLS.PAGERDUTY_INSTALL,
    },
    isPro: true,
  },
  {
    key: "incidentio",
    label: "Incident.io",
    icon: IncidentIoIconUrl,
    docs: {
      install: DOCS_URLS.INCIDENTIO_INSTALL,
    },
    isPro: true,
    availableComponents: ["root"],
  },
  {
    key: "grafana-irm",
    label: "Grafana IRM",
    icon: GrafanaIrmIconUrl,
    docs: {
      install: DOCS_URLS.GRAFANA_IRM_INSTALL,
    },
    isPro: true,
    availableComponents: ["root"],
  },
];

export const notifiers: IntegrationData[] = [
  {
    key: "slack",
    label: "Slack",
    icon: <SlackLogo />,
    docs: {
      install: DOCS_URLS.SLACK_INSTALL,
    },
  },
  {
    key: "ms-teams",
    label: "Microsoft Teams",
    icon: <TeamsLogo />,
    docs: {
      install: DOCS_URLS.TEAMS_INSTALL,
    },
  },
  {
    key: "aws-lambda-notifier",
    label: "AWS Lambda",
    icon: AwsLambdaIconUrl,
    availableComponents: visibleComponentKeys(
      AwsLambdaNotifierComponents,
      getEnvironment().environmentType
    ),
    docs: {
      install: DOCS_URLS.AWS_LAMBDA_NOTIFIER_INSTALL,
    },
  },
  {
    key: "email",
    label: "Email",
    icon: (
      <MailFilled
        /* eslint-disable-line react/forbid-component-props */ style={{
          fontSize: 20,
        }}
      />
    ),
    docs: {
      install: DOCS_URLS.EMAIL_INSTALL,
    },
  },
];

export const directories = (flags: LDFlagSet) =>
  registryToIntegrationData(flags, "directory", DOCS_URLS.DIRECTORY);

export const resourceIntegrations = (flags: LDFlagSet) =>
  registryToIntegrationData(flags, "resource", DOCS_URLS.RESOURCE);

export const allGaIntegrations = Object.fromEntries(
  [
    ...approvers,
    ...directories({}),
    ...notifiers,
    ...resourceIntegrations({}),
    ...siems,
    jira,
    jiraTracker,
  ].map((integration) => [integration.key, integration] as const)
);
